import { useCallback, useEffect, useState } from 'react';
import { getMyPractitionerProfile, setPractitionerLegalName } from '../lib/api';
import type { QiPractitionerProfile } from '../lib/types';
import {
  AUTHORIZATION_STATUS_LABELS,
  OBSERVATION_STATUS_LABELS,
  PRACTITIONER_STATUS_LABELS,
  QUALIFICATION_STATUS_LABELS,
  SACRED_LIGHT_STATUS_LABELS,
  SUBFIELD_STATUS_LABELS,
} from '../lib/constants';

export default function PractitionerProfile({ userId }: { userId: string }) {
  const [profile, setProfile] = useState<QiPractitionerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProfile(await getMyPractitionerProfile());
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveName = useCallback(async () => {
    if (!name.trim()) return;
    setSaving(true);
    setMsg(null);
    setError(null);
    try {
      await setPractitionerLegalName(userId, name.trim());
      setMsg('實名已更新。');
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '設定失敗');
    } finally {
      setSaving(false);
    }
  }, [name, userId, load]);

  if (loading) {
    return (
      <div className="py-6 text-center">
        <div className="w-6 h-6 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
      </div>
    );
  }

  const status = profile?.practitioner_status ?? null;
  const statusLabel = status
    ? PRACTITIONER_STATUS_LABELS[status] || status
    : profile?.exists
      ? QUALIFICATION_STATUS_LABELS[profile.qualification_status || ''] || profile.qualification_status
      : '尚未建立資格';

  return (
    <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center">
            <i className="ri-user-star-line text-base text-background-50" />
          </span>
          <div>
            <h2 className="text-base text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
              施炁者資格與實名
            </h2>
            <p className="text-xs text-foreground-400">你的施炁者資格狀態與實名登記。</p>
          </div>
        </div>
        <span
          className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
            status === 'authorized'
              ? 'bg-accent-100 text-accent-800'
              : status === 'candidate' || status === 'mentor_review'
                ? 'bg-secondary-100 text-secondary-900'
                : 'bg-background-200 text-foreground-600'
          }`}
        >
          {statusLabel}
        </span>
      </div>

      {profile?.exists && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <StatusPill
            label="操作授權"
            value={
              AUTHORIZATION_STATUS_LABELS[profile.practice_authorization_status || ''] ||
              profile.practice_authorization_status
            }
          />
          <StatusPill
            label="觀察狀態"
            value={
              OBSERVATION_STATUS_LABELS[profile.observation_status || ''] || profile.observation_status
            }
          />
          <StatusPill
            label="聖光"
            value={SACRED_LIGHT_STATUS_LABELS[profile.sacred_light_status || ''] || profile.sacred_light_status}
          />
          <StatusPill
            label="子場"
            value={SUBFIELD_STATUS_LABELS[profile.subfield_status || ''] || profile.subfield_status}
          />
        </div>
      )}

      <div className="border-t border-foreground-200/50 pt-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <i className="ri-id-card-line text-foreground-500" />
          <span className="text-xs text-foreground-500">實名（加密保存，僅你與管理員可見）</span>
          {profile?.legal_name ? (
            <span className="text-sm font-medium text-foreground-800">{profile.legal_name}</span>
          ) : (
            <span className="text-xs text-foreground-400">尚未登記</span>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveName();
          }}
          className="flex items-center gap-2 flex-wrap"
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="輸入你的真實姓名"
            className="flex-1 min-w-[200px] px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
          />
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? '處理中…' : '設定實名'}
          </button>
        </form>

        {msg && <p className="text-xs text-emerald-600">{msg}</p>}
        {error && <p className="text-xs text-rose-600">{error}</p>}
      </div>
    </div>
  );
}

function StatusPill({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="bg-background-200/70 rounded-lg px-3 py-2">
      <p className="text-foreground-400">{label}</p>
      <p className="text-foreground-700 mt-0.5">{value || '—'}</p>
    </div>
  );
}